import { useLocation, useNavigate } from '@reach/router';
import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import { SubscribeApi } from 'api/subscribeApi';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { AppPath } from 'appConstants';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import FormStripeCardField from 'components/formComponents/FormStripeCardField';
import FormTextField from 'components/formComponents/FormTextField';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';
import { PackagePromo } from 'subscriptionOptions/types';
import { Form, Formik, FormikHelpers } from 'formik';
import { useToast } from 'hooks/useToast';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Elements, injectStripe } from 'react-stripe-elements';
import {
  selectAddOrUpgradeUserDetails,
  selectFinanceEmail,
  selectPoNumber,
  selectPrice,
  selectRegistrationToken,
  selectSecondExistingUser,
  selectSecondUserDetails,
  selectSelectedPackages,
  selectQuotesId,
  selectQuotePromoCodeSlots,
  selectMembershipOptInfo,
  selectSchoolAccountContext,
} from 'reducers/misc';
import { selectSignedIn } from 'reducers/auth';
import * as Yup from 'yup';

type PaymentDetailsFormProps = { onSubmit: () => void };

type InnerPaymentDetailsFormProps = {
  onSubmit: () => void;
  stripe?: any;
};

const InnerPaymentDetailsForm: React.FC<InnerPaymentDetailsFormProps> = ({
  onSubmit,
  stripe,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const handleToast = useToast('Your payment was successful');

  const [error, setError] = useState<string>('');
  const priceInfo = useSelector(selectPrice);
  const selectedPackages = useSelector(selectSelectedPackages);
  const registrationToken = useSelector(selectRegistrationToken);
  const poNumber = useSelector(selectPoNumber);
  const FinanceEmail = useSelector(selectFinanceEmail);
  const secondUserDetails = useSelector(selectSecondUserDetails);
  const secondExistingUserDetails = useSelector(selectSecondExistingUser);
  const addOrUpgradeUserDetails = useSelector(selectAddOrUpgradeUserDetails);
  const existingMembershipOptInfo = useSelector(selectMembershipOptInfo);
  const quotesId: number | null = useSelector(selectQuotesId);
  const promoCodeSlots = useSelector(selectQuotePromoCodeSlots);
  const isSignedIn = useSelector(selectSignedIn);
  const storedAccountContext = useSelector(selectSchoolAccountContext);
  const schoolAccountContext = isSignedIn && pathname === AppPath.NEW_SUBSCRIPTION ? storedAccountContext : null;

  /** The pairs the summary priced, so payment sends what produced the amount shown. */
  const [packagePromos, setPackagePromos] = useState<PackagePromo[]>([]);

  const quotePackages =
    existingMembershipOptInfo && existingMembershipOptInfo[0]
      ? existingMembershipOptInfo[0].quotePackages
      : null;

  const initialValues = {
    cardholderName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
  };

  const validationSchema = Yup.object({
    cardholderName: Yup.string().required("Please enter the cardholder's name"),
  });

  const createStripeToken = (
    values: any,
    { setSubmitting }: FormikHelpers<any>
  ) =>
    stripe
      .createToken({ name: values.cardholderName })
      .then(({ token, error }: any) => {
        if (token) {
          upgradeByCard(token, setSubmitting);
        }
        if (error) {
          setSubmitting(false);
          setError('Sorry, something went wrong. Please try again.');
        }
      });

  const upgradeByCard = async (
    token: any,
    setSubmitting: (status: boolean) => void
  ) => {
    // Never pay before the summary has priced: the promos it sends are the ones it priced.
    if (packagePromos.length === 0) {
      setSubmitting(false);
      setError('Sorry, something went wrong. Please try again.');
      return;
    }

    let result;
    if (AppPath.SUBSCRIBE === pathname) {
      result = await SubscribeApi.subscribeByCard(
        quotesId,
        packagePromos,
        priceInfo.total,
        token,
        registrationToken,
        poNumber,
        FinanceEmail
      );
    } else {
      result = await NewSubscriptionApi.subscribeByCard(
        packagePromos,
        priceInfo.total,
        token,
        poNumber,
        FinanceEmail,
        secondUserDetails,
        secondExistingUserDetails,
        addOrUpgradeUserDetails
      );
    }

    if (result.data) {
      const status = result.data.status;
      if (status === 'succeeded') {
        if (AppPath.SUBSCRIBE !== pathname) {
          checkFreeTrialAvailable();
        }

        setTimeout(function () {
          handleToast();
          AppPath.SUBSCRIBE === pathname ? onSubmit() : onSubmit();
        }, 2000);
      }

      if (status === 'requires_action') {
        const clientSecret = result.data.clientSecret;
        const tid = result.data.tid;
        handleCardAction(clientSecret, setSubmitting, tid);
      }
    }
    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
    }
  };

  const handleCardAction = (
    clientSecret: string,
    setSubmitting: (status: boolean) => void,
    tid: string
  ) => {
    stripe.handleCardAction(clientSecret).then((res: any) => {
      if (res.error) {
        setSubmitting(false);
        setError(res.error.message);
        return;
      }
      updateAfte3DSecure(res, tid, setSubmitting);
    });
  };

  const updateAfte3DSecure = async (
    values: any,
    tid: string,
    setSubmitting: (status: boolean) => void
  ) => {
    let result;

    if (AppPath.SUBSCRIBE === pathname) {
      result = await SubscribeApi.updateAfter3DSecure({
        quotesId,
        registrationToken,
        ...values,
        tid,
        packageIDs: packagePromos.map((promo) => promo.packagesId),
      });
    } else {
      result = await NewSubscriptionApi.updateAfter3DSecure({
        ...values,
        tid,
      });
    }

    if (result.error) {
      setSubmitting(false);
      setError(result.error.message);
    } else {
      AppPath.SUBSCRIBE === pathname ? onSubmit() : navigate(AppPath.DASHBOARD);
    }
  };

  const handleFormSubmition = (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    createStripeToken(values, formikHelpers);
  };

  const checkFreeTrialAvailable = async () => {
    const result = await SubscriptionsApi.getSubscriptionsList();

    if (result.data) {
      const { bCanAddFreeTrial } = result.data;

      if (
        !bCanAddFreeTrial &&
        document.getElementsByClassName('HeaderDropdownMenu')[0] &&
        document
          .getElementsByClassName('HeaderDropdownMenu')[0]
          .getElementsByClassName('LinkItem UpgradeLink')[0]
      ) {
        document
          .getElementsByClassName('HeaderDropdownMenu')[0]
          .getElementsByClassName('LinkItem UpgradeLink')[0]
          .remove();
      }
    }
  };

  return (
    <div className="Form">
      <FormGeneralError message={error} />
      <Formik
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) =>
          handleFormSubmition(values, formikHelpers)
        }
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="PricingColRow">
              <div className="PCRowInner">
                <div className="LeftCol">
                  <FormTextField
                    field={'cardholderName'}
                    label={"Cardholder's name"}
                    required={true}
                  />
                  <FormStripeCardField
                    field={'card'}
                    label={'Card '}
                    required={true}
                  />
                  {/* <div className="SideBySideDifferent">
                    <FormTextField
                      field={'expiryDate'}
                      label={'Expiry date'}
                      placeholder={'mm/yyyy'}
                      required={true}
                    />
                    <FormTextField field={'cvv'} label={'CVV'} required={true} />
                  </div> */}
                </div>
                <div className="RightCol LowerBlock">
                  <PricingSummaryDetails
                    addPromoStep={false}
                    isDisabled={isSubmitting}
                    isSubmitting={isSubmitting}
                    btnText="Subscribe"
                    existingMembershipOptInfo={existingMembershipOptInfo}
                    accountContext={schoolAccountContext}
                    onPricedPromos={setPackagePromos}
                    {...(quotePackages && quotePackages.length > 0
                      ? { promoCodeSlots, quotePackages }
                      : {})}
                  />
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const PaymentDetailsFormWithStripe = injectStripe(InnerPaymentDetailsForm);

const PaymentDetailsForm: React.FC<PaymentDetailsFormProps> = (props) => {
  return (
    <Elements>
      <PaymentDetailsFormWithStripe {...props} />
    </Elements>
  );
};
export default PaymentDetailsForm;
